import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import ReactFlow, {
  ReactFlowProvider,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  type Connection,
  type Edge,
  type Node,
  type NodeTypes,
  type ReactFlowInstance,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, FloppyDisk, Play, Pause, CaretDown, Trash, PaperPlaneTilt, Clock, BookmarkSimple } from '@phosphor-icons/react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { DotLoader } from '@/components/ui/DotLoader'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { toast } from 'sonner'
import { usePermission } from '@/hooks/usePermission'
import WorkflowSidebar from './components/WorkflowSidebar'
import NodeConfigPanel from './components/NodeConfigPanel'
import CanvasChatPanel from './components/CanvasChatPanel'
import { SaveAsTemplateDialog } from './components/SaveAsTemplateDialog'
import { SubmitForApprovalDialog } from './components/SubmitForApprovalDialog'
import { describeSaveResult, saveNeedsResubmitWarning } from './resubmitOnEdit'
import RejectionBanner from '@/components/approvals/RejectionBanner'
import { TriggerNode, SendEmailNode, DelayNode, ConditionNode, ActionNode, ABTestNode, GoalNode, GenerateDeckNode, EnrichContactsNode, InternalAlertNode, ExitWorkflowNode, CreateTaskNode, HttpRequestNode, RateLimitNode, BranchByFieldNode, LinkedInActionNode, SmsMessageNode, AssignOwnerNode, AiDecisionNode, AutoReplyNode } from './components/nodes'
import { AUTO_REPLY_DEFAULT, normalizeAutoReply, validateAutoReply } from './autoReplyConfig'
import CrmSyncNode from './components/nodes/CrmSyncNode'
import ScheduleMeetingNode from './components/nodes/ScheduleMeetingNode'
import { useWorkflowStore } from '@/stores/workflowStore'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { useSettingsStore } from '@/stores/settingsStore'
import type { WorkflowNodeData, NodeType, AIChatMessage } from '@/types/workflow'
import type { ApprovalInfo } from '@/types/approvals'
import { getUpstreamEnrichmentFields } from './workflow-utils'
import { nodeHex, nodeLabel } from './nodeCatalog'

// Generate unique ID
let idCounter = 0
const getId = () => `node_${Date.now()}_${idCounter++}`

// Human-readable status badge text — most statuses already read fine
// title-cased (draft/active/paused/completed/stopped), but
// "pending_approval" needs an actual label, not `capitalize` on the raw
// underscored string.
const WORKFLOW_STATUS_LABELS: Record<string, string> = {
  pending_approval: 'Pending approval',
  rejected: 'Rejected',
}

// Shown as the static header label throughout a template-authoring session
// (there's no editable "workflow name" field there -- see templateAuthoring)
// AND as the pre-filled name in the "Save as template" dialog it opens, so
// the two stay in sync instead of drifting.
const DEFAULT_TEMPLATE_NAME = 'Untitled Template'

// Labels come from the node catalog so a new node is named exactly what the
// palette, the config panel header and the MiniMap call it. Only the label is
// shared — every `*_config` default below stays local to the builder.
const defaultNodeData: Record<string, WorkflowNodeData> = {
  trigger: { label: nodeLabel('trigger'), trigger_type: 'manual' },
  // Enrichment contact_fields are not defaulted here — the config panel offers
  // (and auto-selects) them only when an Enrich Contacts node runs upstream.
  sendEmail: { label: nodeLabel('sendEmail'), subject: '', body: '', ai_config: { generation_mode: 'manual', material_ids: [], contact_fields: ['job_title', 'company_name', 'company_industry'], tone: 'professional', purpose: 'introduction', custom_instructions: '' } },
  delay: { label: nodeLabel('delay'), delay_value: 1, delay_unit: 'days', delay_mode: 'duration', business_days_only: false },
  condition: { label: nodeLabel('condition'), condition_type: 'email_opened' },
  action: { label: nodeLabel('action'), action_type: 'tag_contact' },
  abTest: { label: nodeLabel('abTest'), ab_test_config: { variants: [{ id: 'variant_1', label: 'Variant 1', percentage: 50 }, { id: 'variant_2', label: 'Variant 2', percentage: 50 }] } },
  goal: { label: nodeLabel('goal'), goal_config: { goal_type: 'replied' } },
  generateDeck: { label: nodeLabel('generateDeck'), generate_deck_config: { mode: 'ai_generate', material_ids: [], contact_fields: ['job_title', 'company_name', 'company_industry'], slide_count: 6, content_focus: 'pitch_deck', tone: 'professional', custom_instructions: '', color_theme: 'auto_from_brand', logo_url: '' } },
  // `providers` defaults to Native (AI) enabled so the config panel checkbox
  // shows it selected the moment the node is dropped (AB#22321) — an empty
  // array reads as "no provider selected" in EnrichContactsConfig even though
  // the backend's backward-compat fallback still runs native enrichment.
  enrichContacts: { label: nodeLabel('enrichContacts'), enrich_contacts_config: { skip_already_enriched: true, max_age_days: 0, enabled_blocks: ['person', 'company', 'sector', 'economy', 'outreach_hooks', 'buying_signals', 'communication_prefs', 'competitive_intel', 'content_relevance', 'relationship_map'], providers: [{ name: 'native', enabled: true, config: {} }] } },
  internalAlert: { label: nodeLabel('internalAlert'), internal_alert_config: { channel: 'email', message_template: '', include_contact_details: true } },
  exitWorkflow: { label: nodeLabel('exitWorkflow'), exit_workflow_config: { reason: 'converted' } },
  createTask: { label: nodeLabel('createTask'), create_task_config: { task_type: 'follow_up', title_template: '', description_template: '', priority: 'medium', due_in_days: 1 } },
  httpRequest: { label: nodeLabel('httpRequest'), http_request_config: { url: '', method: 'POST', headers: {}, body_template: '{}', timeout_seconds: 10, store_response: false } },
  rateLimit: { label: nodeLabel('rateLimit'), rate_limit_config: { mode: 'daily_cap', max_per_day: 50 } },
  branchByField: { label: nodeLabel('branchByField'), branch_by_field_config: { branches: [{ id: 'branch_1', label: 'Branch 1', field: '', operator: 'equals', value: '' }], has_default: true } },
  linkedInAction: { label: nodeLabel('linkedInAction'), linkedin_action_config: { action_type: 'connect', message_template: '' } },
  smsMessage: { label: nodeLabel('smsMessage'), sms_message_config: { message_template: '' } },
  assignOwner: { label: nodeLabel('assignOwner'), assign_owner_config: { team_member_emails: [], assignment_mode: 'round_robin' } },
  aiDecision: { label: nodeLabel('aiDecision'), ai_decision_config: { decision_prompt: '', branches: [{ id: 'branch_1', label: 'Branch 1', description: '' }], contact_fields: [] } },
  crmSync: { label: nodeLabel('crmSync'), crm_sync_config: { provider: 'hubspot', sync_contact: true, create_deal: false, deal_name_template: '', deal_stage: '', field_mapping: {} } },
  scheduleMeeting: { label: nodeLabel('scheduleMeeting'), schedule_meeting_config: { provider: 'zoom', topic_template: '{{contact.company_name}} — Quick Chat', duration: 30, agenda_template: '', include_contact_as_attendee: true, calendar_id: 'primary', outlook_user_id: 'me' } },
  autoReply: { label: nodeLabel('autoReply'), auto_reply_config: AUTO_REPLY_DEFAULT },
}

/** Stable serialization of the editable workflow state — used to detect unsaved edits. */
function buildWorkflowSnapshot(
  name: string,
  description: string,
  nodes: { id: string; type?: string; position: { x: number; y: number }; data: unknown }[],
  edges: { id: string; source: string; target: string; sourceHandle?: string | null; targetHandle?: string | null }[],
): string {
  return JSON.stringify({
    name,
    description,
    nodes: nodes.map((n) => ({ id: n.id, type: n.type, position: n.position, data: n.data })),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: e.sourceHandle ?? null,
      targetHandle: e.targetHandle ?? null,
    })),
  })
}

const WorkflowBuilderInner: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  // When the builder was opened via "Use template", returning should land back
  // on the Templates page (workflow tab), not the Workflows list.
  const cameFromTemplates = (location.state as { from?: string } | null)?.from === 'templates'
  // Set only when opened via the Templates screen's "Create template" button
  // (WorkflowTemplateGallery.tsx) -- a dedicated authoring session whose only
  // purpose is producing a template, not a real campaign workflow. The
  // regular Save/Activate/Pause actions and the "Save as template" action
  // are mutually exclusive: the latter only ever appears in this mode.
  const templateAuthoring = (location.state as { templateAuthoring?: boolean } | null)?.templateAuthoring === true
  const exitToList = () => {
    if (cameFromTemplates) navigate('/templates', { state: { tab: 'workflow' } })
    else navigate('/workflows')
  }
  const reactFlowWrapper = useRef<HTMLDivElement>(null)
  const nameInputRef = useRef<HTMLInputElement>(null)
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null)
  // Bound to the ReactFlowProvider context, not to the onInit callback below --
  // available the instant this component mounts, unlike `reactFlowInstance`
  // (which only exists once React Flow finishes its own internal init, one or
  // two renders after the canvas first becomes visible). onDrop below used to
  // require `reactFlowInstance` purely to call `.project()`; a drop that lands
  // in that brief gap silently did nothing -- no error, nothing added -- which
  // is exactly what it looks like when a user drags a node in right as a
  // workflow (e.g. one just reopened after a rejection) finishes loading.
  const { screenToFlowPosition } = useReactFlow()

  const [nodes, setNodes, onNodesChangeRaw] = useNodesState([])
  const [edges, setEdges, onEdgesChangeRaw] = useEdgesState([])
  const [selectedNode, setSelectedNode] = useState<Node<WorkflowNodeData> | null>(null)
  const [workflowName, setWorkflowName] = useState('Untitled Workflow')
  const [showNameMenu, setShowNameMenu] = useState(false)
  const [workflowDescription, setWorkflowDescription] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [workflowId, setWorkflowId] = useState<number | null>(id ? parseInt(id) : null)
  const [workflowStatus, setWorkflowStatus] = useState<string>('draft')
  // Latest approval record for the loaded workflow: feeds the rejection
  // banner so a Rep sees why it came back. Cleared on resubmit so a stale
  // rejection never lingers over a workflow that is pending again.
  const [workflowApproval, setWorkflowApproval] = useState<ApprovalInfo | null>(null)
  const [showDiscard, setShowDiscard] = useState(false)
  // A Rep saving a live workflow: confirm first, since changed steps go
  // back for approval (AB#22468).
  const [showResubmitConfirm, setShowResubmitConfirm] = useState(false)
  // Freshly AI-generated workflows open as an unsaved draft: the user saves to
  // keep it or discards to delete it. Read once from nav state (lost on reload).
  const aiCreatedRef = useRef((location.state as { aiCreated?: boolean } | null)?.aiCreated === true)
  const aiInitRef = useRef(false) // one-shot guard (StrictMode runs effects twice)
  const [isAiDraft, setIsAiDraft] = useState(aiCreatedRef.current)
  // AI generation/edit transcript — seeded from the saved workflow, continued
  // by the in-canvas chat, and persisted on save.
  const [aiChatHistory, setAiChatHistory] = useState<AIChatMessage[]>([])
  // Nodes the last AI chat edit added/changed — briefly outlined on the canvas.
  const [recentlyChangedIds, setRecentlyChangedIds] = useState<Set<string>>(new Set())
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current) }, [])

  // Unsaved-changes guard (design §18): snapshot the saved state, compare on exit.
  const initialSnapshotRef = useRef<string>('')
  const currentSnapshot = useMemo(
    () => buildWorkflowSnapshot(workflowName, workflowDescription, nodes as any, edges as any),
    [workflowName, workflowDescription, nodes, edges],
  )
  const isDirty = initialSnapshotRef.current !== '' && currentSnapshot !== initialSnapshotRef.current

  // ── Undo / redo history ───────────────────────────────────────────────
  // ReactFlow has no built-in history. Keep a past/future stack of
  // {nodes, edges} snapshots. Snapshots are taken BEFORE a mutating action
  // (drag start, connect, drop, delete, config edit) so undo restores the
  // state prior to that action.
  type HistoryEntry = { nodes: Node[]; edges: Edge[] }
  const nodesRef = useRef<Node[]>(nodes)
  const edgesRef = useRef<Edge[]>(edges)
  nodesRef.current = nodes
  edgesRef.current = edges
  const pastRef = useRef<HistoryEntry[]>([])
  const futureRef = useRef<HistoryEntry[]>([])

  const takeSnapshot = useCallback(() => {
    pastRef.current = [
      ...pastRef.current,
      { nodes: nodesRef.current, edges: edgesRef.current },
    ].slice(-50) // cap history depth
    futureRef.current = []
  }, [])

  const undo = useCallback(() => {
    const past = pastRef.current
    if (past.length === 0) return
    const previous = past[past.length - 1]
    pastRef.current = past.slice(0, -1)
    futureRef.current = [...futureRef.current, { nodes: nodesRef.current, edges: edgesRef.current }]
    setNodes(previous.nodes)
    setEdges(previous.edges)
    setSelectedNode(null)
  }, [setNodes, setEdges])

  const redo = useCallback(() => {
    const future = futureRef.current
    if (future.length === 0) return
    const next = future[future.length - 1]
    futureRef.current = future.slice(0, -1)
    pastRef.current = [...pastRef.current, { nodes: nodesRef.current, edges: edgesRef.current }]
    setNodes(next.nodes)
    setEdges(next.edges)
    setSelectedNode(null)
  }, [setNodes, setEdges])

  // Wrap the raw change handlers to snapshot before a node/edge removal
  // (covers the Backspace/Delete key path that bypasses our own handlers).
  const onNodesChange = useCallback(
    (changes: any[]) => {
      if (changes.some((c) => c.type === 'remove')) takeSnapshot()
      onNodesChangeRaw(changes)
    },
    [onNodesChangeRaw, takeSnapshot],
  )
  const onEdgesChange = useCallback(
    (changes: any[]) => {
      if (changes.some((c) => c.type === 'remove')) takeSnapshot()
      onEdgesChangeRaw(changes)
    },
    [onEdgesChangeRaw, takeSnapshot],
  )

  // Seed a brand-new workflow (no id → nothing to load from the server).
  // When opened via "Use template", the chosen template is passed through
  // navigation state and pre-loaded as an UNSAVED draft. Creating from a
  // template is allowed even when a workflow with the same name already
  // exists — the duplicate-name check fires only at save time (backend 409),
  // so the user must rename before the workflow can be saved.
  useEffect(() => {
    if (id) return
    const template = (location.state as { template?: {
      name: string
      description?: string
      nodes?: Node[]
      edges?: Edge[]
    } } | null)?.template
    if (template) {
      const tplNodes = (template.nodes || []) as Node[]
      const tplEdges = (template.edges || []) as Edge[]
      setWorkflowName(template.name)
      setWorkflowDescription(template.description || '')
      setNodes(tplNodes)
      setEdges(tplEdges)
      // Seed the saved-state baseline from the template so the dirty flag only
      // trips on actual user edits, mirroring the load-by-id path.
      initialSnapshotRef.current = buildWorkflowSnapshot(
        template.name,
        template.description || '',
        tplNodes as any,
        tplEdges as any,
      )
    } else {
      initialSnapshotRef.current = buildWorkflowSnapshot('Untitled Workflow', '', [], [])
    }
  }, [])

  const handleBack = () => {
    if (isDirty) setShowDiscard(true)
    else exitToList()
  }

  // Browser-level guard (tab close / refresh / browser back / URL change).
  // Router-agnostic; shows the native "Leave site?" prompt while there are
  // unsaved edits. Custom in-app blocking would require the data router.
  useEffect(() => {
    if (!isDirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  const { createWorkflow, updateWorkflow, fetchWorkflowById, activateWorkflow, deactivateWorkflow, deleteWorkflow } = useWorkflowStore()
  // A Rep's own "Activate" request is queued for approval, not immediate --
  // see handleActivate below and backend/app/routes/workflows.py.
  const isRep = useAuthStore((s) => s.user?.role) === 'representative'
  // Whoever can create a workflow can create a template -- same cell as
  // regular workflow creation, not a separate one (see WorkflowTemplateGallery.tsx).
  // Write access on workflows.manage: gates "Save as template" and Save
  // itself. Analyst's level is 'view' (spec 4-H), and POST/PUT /workflows
  // 403 for them, so Save is disabled rather than failing on click
  // (AB#22339). The list page hides the entry point; this covers a
  // hand-typed /workflows/builder.
  const { canWrite: canWriteWorkflows } = usePermission('workflows', 'manage')
  // Activate / Pause live behind their own cell, which an Analyst holds
  // not at all -- offering them on a read-only workflow is the same
  // click-into-403 as Save was (AB#22339).
  const { allowed: canActivate } = usePermission('workflows', 'activate')
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false)
  const [showSubmitForApproval, setShowSubmitForApproval] = useState(false)
  const isDark = useThemeStore((s) => s.actualTheme === 'dark')
  const settingsConfig = useSettingsStore((s) => s.config)
  const fetchSettingsConfig = useSettingsStore((s) => s.fetchConfig)
  useEffect(() => {
    // Needed to validate Slack/Teams alert nodes against saved Settings webhooks.
    if (!settingsConfig) fetchSettingsConfig()
  }, [settingsConfig, fetchSettingsConfig])

  const nodeTypes: NodeTypes = useMemo(() => ({
    trigger: TriggerNode,
    sendEmail: SendEmailNode,
    delay: DelayNode,
    condition: ConditionNode,
    action: ActionNode,
    abTest: ABTestNode,
    goal: GoalNode,
    generateDeck: GenerateDeckNode,
    enrichContacts: EnrichContactsNode,
    internalAlert: InternalAlertNode,
    exitWorkflow: ExitWorkflowNode,
    createTask: CreateTaskNode,
    httpRequest: HttpRequestNode,
    rateLimit: RateLimitNode,
    branchByField: BranchByFieldNode,
    linkedInAction: LinkedInActionNode,
    smsMessage: SmsMessageNode,
    assignOwner: AssignOwnerNode,
    aiDecision: AiDecisionNode,
    crmSync: CrmSyncNode,
    scheduleMeeting: ScheduleMeetingNode,
    autoReply: AutoReplyNode,
  }), [])

  // Compute upstream enrichment fields for each node and inject into data
  const enrichedNodes = useMemo(() => {
    const workflowNodes = nodes.map(n => ({ id: n.id, type: n.type as NodeType, position: n.position, data: n.data as WorkflowNodeData }))
    const workflowEdges = edges.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle || undefined, targetHandle: e.targetHandle || undefined }))
    return nodes.map(node => {
      const upstreamFields = getUpstreamEnrichmentFields(node.id, workflowNodes, workflowEdges)
      const changed = recentlyChangedIds.has(node.id)
      const highlight = changed
        ? { style: { ...(node.style || {}), outline: '2px solid #5e6ad2', outlineOffset: 4, borderRadius: 14 } }
        : null
      if (upstreamFields.length > 0 || highlight) {
        return {
          ...node,
          ...(highlight || {}),
          data: upstreamFields.length > 0
            ? { ...node.data, _upstreamEnrichmentFields: upstreamFields }
            : node.data,
        }
      }
      return node
    })
  }, [nodes, edges, recentlyChangedIds])

  // Style edges from enrichContacts nodes with cyan color
  const styledEdges = useMemo(() => {
    const enrichNodeIds = new Set(nodes.filter(n => n.type === 'enrichContacts').map(n => n.id))
    return edges.map(edge => {
      if (enrichNodeIds.has(edge.source)) {
        return {
          ...edge,
          style: { stroke: '#06b6d4', strokeWidth: 2.5 },
          animated: true,
        }
      }
      return edge
    })
  }, [nodes, edges])

  // Load existing workflow
  useEffect(() => {
    if (id) {
      setIsLoading(true)
      fetchWorkflowById(parseInt(id))
        .then((workflow) => {
          setWorkflowName(workflow.name)
          setWorkflowDescription(workflow.description || '')
          setWorkflowStatus(workflow.status)
          setWorkflowApproval(workflow.approval ?? null)
          setWorkflowId(workflow.id)
          if (workflow.nodes && workflow.nodes.length > 0) {
            setNodes(workflow.nodes as Node[])
          }
          if (workflow.edges && workflow.edges.length > 0) {
            setEdges(workflow.edges as Edge[])
          }
          setAiChatHistory(((workflow as any).ai_chat_history as AIChatMessage[]) || [])
          // Seed the saved-state snapshot from the freshly loaded data so the
          // dirty check only fires on user edits, not on the initial load.
          if (aiCreatedRef.current) {
            // AI-created draft: force the unsaved state so it must be saved to keep.
            // Set on every load run (incl. StrictMode's 2nd) so the baseline never
            // gets reset to the data snapshot, which would clear the dirty flag.
            initialSnapshotRef.current = '__ai_draft_unsaved__'
            if (!aiInitRef.current) {
              aiInitRef.current = true
              toast.success('Workflow created')
            }
          } else {
            // Seed the saved-state snapshot so the dirty check only fires on edits.
            initialSnapshotRef.current = buildWorkflowSnapshot(
              workflow.name,
              workflow.description || '',
              (workflow.nodes || []) as any,
              (workflow.edges || []) as any,
            )
          }
        })
        .catch(() => {
          toast.error('Failed to load workflow')
          exitToList()
        })
        .finally(() => setIsLoading(false))
    }
  }, [id])

  const onConnect = useCallback(
    (params: Connection) => {
      takeSnapshot()
      setEdges((eds) => addEdge({ ...params, animated: true, type: 'smoothstep' }, eds))
    },
    [setEdges, takeSnapshot]
  )

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault()
      const type = event.dataTransfer.getData('application/reactflow')
      if (!type) return

      // screenToFlowPosition takes raw client coordinates and resolves them
      // against the pane itself -- no manual bounding-rect subtraction, and
      // no dependency on `reactFlowInstance`/onInit having fired yet (see the
      // comment on `screenToFlowPosition` above).
      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY })

      const newNode: Node<WorkflowNodeData> = {
        id: getId(),
        type,
        position,
        data: { ...defaultNodeData[type] },
      }

      takeSnapshot()
      setNodes((nds) => nds.concat(newNode))
    },
    [screenToFlowPosition, setNodes, takeSnapshot]
  )

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node as Node<WorkflowNodeData>)
  }, [])

  const onPaneClick = useCallback(() => {
    setSelectedNode(null)
  }, [])

  const handleNodeUpdate = useCallback((nodeId: string, data: Partial<WorkflowNodeData>) => {
    takeSnapshot()
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return { ...node, data: { ...node.data, ...data } }
        }
        return node
      })
    )
    // Update selected node reference
    setSelectedNode((prev) => {
      if (prev && prev.id === nodeId) {
        return { ...prev, data: { ...prev.data, ...data } }
      }
      return prev
    })
  }, [setNodes, takeSnapshot])

  const handleNodeDelete = useCallback((nodeId: string) => {
    takeSnapshot()
    setNodes((nds) => nds.filter((n) => n.id !== nodeId))
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
    setSelectedNode(null)
  }, [setNodes, setEdges, takeSnapshot])

  const onNodesDelete = useCallback((deleted: Node[]) => {
    if (deleted.some(n => n.id === selectedNode?.id)) {
      setSelectedNode(null)
    }
  }, [selectedNode])

  // Apply an AI chat edit: full re-generate + re-layout. Snapshot first so the
  // user can undo the AI change like any other edit. We also reframe the canvas
  // (fitView) and briefly highlight nodes that are new/changed, so edits are
  // visible even when they only touch a node's (off-card) config.
  const applyAiEdit = useCallback((newNodes: any[], newEdges: any[]) => {
    // Signature of the pre-edit canvas: a node is "changed" if its type+label
    // pair didn't exist before (covers additions and relabeled/restructured
    // nodes). Config-only edits keep the same label, so we can't pinpoint those
    // by id — fitView + the change_summary message cover that case.
    const prevSig = new Set(
      (nodesRef.current || []).map((n) => `${n.type}::${(n.data as any)?.label ?? ''}`),
    )
    const changedIds = newNodes
      .filter((n) => !prevSig.has(`${n.type}::${n.data?.label ?? ''}`))
      .map((n) => n.id)

    takeSnapshot()
    setNodes(newNodes as Node[])
    setEdges(newEdges as Edge[])
    setSelectedNode(null)
    setRecentlyChangedIds(new Set(changedIds))

    // Reframe after ReactFlow has the new nodes, then fade the highlight.
    setTimeout(() => reactFlowInstance?.fitView({ duration: 500, padding: 0.2 }), 60)
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    highlightTimerRef.current = setTimeout(() => setRecentlyChangedIds(new Set()), 3500)
  }, [setNodes, setEdges, takeSnapshot, reactFlowInstance])

  // Returns whether the save actually happened — "Save & exit" in the
  // unsaved-changes dialog must not leave the page on a failed/invalid save.
  const handleSave = async (): Promise<boolean> => {
    if (!workflowName.trim()) {
      toast.error('Please enter a workflow name')
      return false
    }

    if (nodes.length === 0) {
      toast.error('Add at least one node before saving')
      return false
    }

    // Nothing changed since the last save — don't re-save (and don't claim
    // "Workflow saved"). Matters especially for active workflows, where a
    // no-op Save read as if something had been modified.
    if (workflowId && !isAiDraft && !isDirty) {
      toast.info('No changes to save')
      return true
    }

    // Nodes reachable from a trigger — disconnected nodes never execute, so
    // they don't satisfy (or trigger) the task-node rules below.
    const adjacency = new Map<string, string[]>()
    for (const e of edges) {
      const children = adjacency.get(e.source) || []
      children.push(e.target)
      adjacency.set(e.source, children)
    }
    const reachable = new Set<string>()
    const stack = nodes.filter((n) => n.type === 'trigger').map((n) => n.id)
    while (stack.length) {
      const id = stack.pop()!
      if (reachable.has(id)) continue
      reachable.add(id)
      for (const child of adjacency.get(id) || []) stack.push(child)
    }

    // Assign Owner only back-fills owners onto tasks produced by Create Task,
    // so a flow with Assign Owner but no CONNECTED Create Task node can never
    // work. Block the save (same rule activation enforces server-side) — a
    // Create Task node sitting disconnected on the canvas doesn't count
    // because it never executes (AB#21849).
    const hasAssignOwner = nodes.some((n) => n.type === 'assignOwner')
    if (hasAssignOwner) {
      const hasConnectedCreateTask = nodes.some(
        (n) => n.type === 'createTask' && reachable.has(n.id)
      )
      if (!hasConnectedCreateTask) {
        toast.error(
          'Assign Owner needs a Create Task node connected to the flow. ' +
          'Connect a Create Task node (from the trigger) or remove Assign Owner before saving.'
        )
        return false
      }
    }

    // Title Template is required on every connected Create Task node
    // (AB#21850). Activation rejects it server-side anyway; blocking here
    // surfaces the problem at Save time with the offending node named.
    const untitledTask = nodes.find(
      (n) =>
        n.type === 'createTask' &&
        reachable.has(n.id) &&
        !((n.data as WorkflowNodeData).create_task_config?.title_template || '').trim()
    )
    if (untitledTask) {
      const label = (untitledTask.data as WorkflowNodeData).label || nodeLabel('createTask')
      toast.error(`"${label}" needs a Title Template before the workflow can be saved.`)
      return false
    }

    // Auto Reply must follow a Send Email, be the last step, and be fully
    // configured. Activation enforces the same server-side; blocking at Save
    // names the node and the first thing to fix.
    for (const n of nodes) {
      if (n.type !== 'autoReply' || !reachable.has(n.id)) continue
      const label = (n.data as WorkflowNodeData).label || nodeLabel('autoReply')
      const fedBySendEmail = edges.some(
        (e) => e.target === n.id && nodes.find((m) => m.id === e.source)?.type === 'sendEmail'
      )
      if (!fedBySendEmail) {
        toast.error(`"${label}" must come right after a Send Email node.`)
        return false
      }
      if (edges.some((e) => e.source === n.id)) {
        toast.error(`"${label}" must be the last step. Remove the connection leaving it.`)
        return false
      }
      const problems = validateAutoReply(normalizeAutoReply((n.data as WorkflowNodeData).auto_reply_config))
      if (problems.length > 0) {
        toast.error(`"${label}" is not ready: ${problems[0]}`)
        return false
      }
    }

    // A/B Test variant percentages must sum to exactly 100 — the executor
    // (campaign_executor.py) uses them as a probability split, so anything
    // else either under- or over-allocates contacts. The config panel only
    // shows a passive red hint when this node is open; block Save too so it
    // can't slip through unnoticed (AB#21564).
    const badAbTest = nodes.find((n) => {
      if (n.type !== 'abTest' || !reachable.has(n.id)) return false
      const variants = (n.data as WorkflowNodeData).ab_test_config?.variants || []
      const total = variants.reduce((sum, v) => sum + (v.percentage || 0), 0)
      return total !== 100
    })
    if (badAbTest) {
      const label = (badAbTest.data as WorkflowNodeData).label || nodeLabel('abTest')
      const variants = (badAbTest.data as WorkflowNodeData).ab_test_config?.variants || []
      const total = variants.reduce((sum, v) => sum + (v.percentage || 0), 0)
      toast.error(
        `"${label}" variant percentages add up to ${total}%, not 100%. ` +
        `Fix the split before the workflow can be saved.`
      )
      return false
    }

    // A branch/variant outlet with no outgoing edge is a silent dead end at
    // runtime: contacts routed to it just exit the workflow, which reads as
    // "the condition didn't work". Warn (don't block — an unwired branch can
    // be an intentional filter).
    for (const n of nodes) {
      const d = n.data as WorkflowNodeData
      let outlets: { id: string; label: string }[] = []
      if (n.type === 'branchByField') {
        const cfg = d.branch_by_field_config
        outlets = (cfg?.branches || []).map((b) => ({ id: b.id, label: b.label || b.id }))
        if (cfg?.has_default !== false) outlets.push({ id: 'default', label: 'Default' })
      } else if (n.type === 'aiDecision') {
        outlets = (d.ai_decision_config?.branches || []).map((b) => ({ id: b.id, label: b.label || b.id }))
      } else if (n.type === 'abTest') {
        outlets = (d.ab_test_config?.variants || []).map((v) => ({ id: v.id, label: v.label || v.id }))
      }
      const unwired = outlets.filter(
        (o) => !edges.some((e) => e.source === n.id && e.sourceHandle === o.id)
      )
      if (unwired.length > 0) {
        toast.warning(
          `"${d.label || n.type}" has unconnected branch${unwired.length > 1 ? 'es' : ''} ` +
          `(${unwired.map((o) => o.label).join(', ')}). Contacts matching ` +
          `${unwired.length > 1 ? 'these branches' : 'this branch'} will exit the workflow without further steps.`
        )
      }
    }

    // Slack/Teams alerts silently no-op at runtime if no webhook is available.
    // Block save until a webhook exists in the node OR Settings → Integrations.
    const unconfiguredAlert = nodes.find((n) => {
      if (n.type !== 'internalAlert') return false
      const cfg = (n.data as WorkflowNodeData).internal_alert_config
      const ch = cfg?.channel
      if (ch !== 'slack' && ch !== 'teams') return false
      const nodeWebhook = ch === 'slack' ? cfg?.slack_webhook_url : cfg?.teams_webhook_url
      const settingsSet = ch === 'slack' ? settingsConfig?.slack_webhook_url_set : settingsConfig?.teams_webhook_url_set
      return !((nodeWebhook || '').trim()) && !settingsSet
    })
    if (unconfiguredAlert) {
      const ch = (unconfiguredAlert.data as WorkflowNodeData).internal_alert_config?.channel
      const label = (unconfiguredAlert.data as WorkflowNodeData).label || nodeLabel('internalAlert')
      toast.error(
        `"${label}" sends a ${ch === 'slack' ? 'Slack' : 'Microsoft Teams'} alert but has no webhook configured. ` +
        `Add one in the node, or connect ${ch === 'slack' ? 'Slack' : 'Microsoft Teams'} in Settings → Integrations.`
      )
      return false
    }

    setIsSaving(true)
    try {
      const data = {
        name: workflowName,
        description: workflowDescription,
        nodes: nodes.map(n => ({ id: n.id, type: n.type as NodeType, position: n.position, data: n.data as WorkflowNodeData })),
        edges: edges.map(e => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle || undefined, targetHandle: e.targetHandle || undefined, animated: e.animated, type: e.type })),
        ai_chat_history: aiChatHistory,
      }

      if (workflowId) {
        const saved = await updateWorkflow(workflowId, data)
        // A Representative's edit to an approved workflow comes back as
        // pending_approval with a fresh approval record; mirror it so the
        // pill, banner and toast agree with the server.
        const nextStatus = (saved?.status as string | undefined) ?? workflowStatus
        toast.success(describeSaveResult(saved ?? {}, workflowStatus))
        if (nextStatus !== workflowStatus) setWorkflowStatus(nextStatus)
        if (saved && 'approval' in saved) setWorkflowApproval(saved.approval ?? null)
      } else {
        const created = await createWorkflow(data)
        setWorkflowId(created.id)
        // Update URL without navigation
        window.history.replaceState(null, '', `/workflows/builder/${created.id}`)
        // Success toast is fired by the store's createWorkflow.
      }
      // Saved state is now the baseline — clear the dirty flag and the draft state.
      initialSnapshotRef.current = currentSnapshot
      setIsAiDraft(false)
      return true
    } catch {
      // Error handled by store
      return false
    } finally {
      setIsSaving(false)
    }
  }

  // Keyboard shortcuts — cross-OS (Cmd on mac, Ctrl elsewhere):
  //   Save  = Cmd/Ctrl+S   Undo = Cmd/Ctrl+Z   Redo = Cmd/Ctrl+Shift+Z or Ctrl+Y
  // Stable refs keep the listener bound once (handlers change every render).
  const saveRef = useRef(handleSave)
  saveRef.current = handleSave
  // In a template-authoring session, Ctrl/Cmd+S must open the "Save as
  // template" dialog instead of handleSave -- there is no real workflow to
  // persist here, so the regular save path would be a silent no-op at best.
  const ctrlSRef = useRef(() => {})
  ctrlSRef.current = () => {
    if (templateAuthoring) {
      if (canWriteWorkflows && nodes.length > 0) setShowSaveAsTemplate(true)
    } else {
      saveRef.current()
    }
  }
  const undoRef = useRef(undo)
  undoRef.current = undo
  const redoRef = useRef(redo)
  redoRef.current = redo

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey
      if (!mod) return
      const key = e.key.toLowerCase()
      if (key === 's') {
        e.preventDefault()
        ctrlSRef.current()
        return
      }
      // While typing in a field, let the browser's native text undo/redo run.
      const t = e.target as HTMLElement | null
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault()
        undoRef.current()
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault()
        redoRef.current()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Direct roles activate in one click; a Rep goes through
  // SubmitForApprovalDialog, which calls this with the chosen approver.
  const handleActivate = async (approverId?: number) => {
    if (!workflowId) return
    // Activation validates the SAVED workflow on the server, so unsaved
    // config edits (e.g. a Title Template typed moments ago) are invisible
    // to it and activation fails even though the builder looks correct.
    // Save first so activation always sees what the user sees.
    if (isDirty || isAiDraft) {
      const saved = await handleSave()
      if (!saved) return
    }
    try {
      const resultStatus = await activateWorkflow(workflowId, approverId)
      setWorkflowStatus(resultStatus)
      // A fresh submission supersedes any earlier rejection record.
      setWorkflowApproval(null)
    } catch {
      /* handled in store */
    }
  }

  // The dialog stays open on failure (the store already toasted), so a
  // failed save or submit must reject rather than resolve quietly.
  const handleSubmitForApproval = async (approverId: number) => {
    if (!workflowId) return
    if (isDirty || isAiDraft) {
      const saved = await handleSave()
      if (!saved) throw new Error('Save failed')
    }
    const resultStatus = await activateWorkflow(workflowId, approverId)
    setWorkflowStatus(resultStatus)
    setWorkflowApproval(null)
  }

  const handleDeactivate = async () => {
    if (!workflowId) return
    try {
      await deactivateWorkflow(workflowId)
      setWorkflowStatus('paused')
    } catch {
      /* handled in store */
    }
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <DotLoader size="lg" label="Loading workflow" />
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Top Bar */}
      <div className="h-14 bg-card border-b border-border/60 flex items-center justify-between gap-3 px-5 flex-shrink-0 z-10">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-accent/30 rounded-lg transition-all duration-150 flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-muted-foreground" />
          </button>
          <div className="h-6 w-px bg-border/60 flex-shrink-0" />
          {templateAuthoring ? (
            // This canvas is never saved as a workflow, so an editable
            // "workflow name" field here would mislead the user into
            // thinking it names the thing they're actually creating. The
            // real name is entered in the "Save as template" dialog instead.
            <span className="text-lg font-emphasis leading-none tracking-tight text-foreground min-w-0 truncate">
              {DEFAULT_TEMPLATE_NAME}
            </span>
          ) : (
            <>
              <input
                ref={nameInputRef}
                type="text"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.currentTarget.select()}
                onBlur={(e) => { if (!e.target.value.trim()) setWorkflowName('Untitled Workflow') }}
                className="text-lg font-emphasis leading-none tracking-tight bg-transparent border-none outline-none text-foreground focus:ring-0 min-w-0 max-w-full py-0 [field-sizing:content]"
                placeholder="Workflow name..."
              />
              <div className="relative flex-shrink-0">
                <button
                  onClick={() => setShowNameMenu((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={showNameMenu}
                  aria-label="Workflow menu"
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent dark:hover:bg-white/[0.06] transition-colors duration-150"
                >
                  <CaretDown className={`h-4 w-4 transition-transform duration-150 ${showNameMenu ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {showNameMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowNameMenu(false)} />
                      <motion.div
                        initial={{ opacity: 0, y: -4, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -4, scale: 0.98 }}
                        transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        role="menu"
                        className="absolute top-full mt-1.5 left-0 origin-top py-1 w-44 z-50 bg-card border border-border/60 rounded-md shadow-lg dark:bg-[#191a1b] dark:border-white/[0.08] dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]"
                      >
                        <button
                          role="menuitem"
                          onClick={() => { setShowNameMenu(false); requestAnimationFrame(() => nameInputRef.current?.focus()) }}
                          className="mx-1 w-[calc(100%-0.5rem)] flex items-center px-2.5 py-1.5 text-left text-[13px] rounded-md text-foreground hover:bg-accent dark:hover:bg-white/[0.06] transition-colors duration-150"
                        >
                          Rename
                        </button>
                        <button
                          role="menuitem"
                          onClick={() => { setShowNameMenu(false); handleBack() }}
                          className="mx-1 w-[calc(100%-0.5rem)] flex items-center px-2.5 py-1.5 text-left text-[13px] rounded-md text-foreground hover:bg-accent dark:hover:bg-white/[0.06] transition-colors duration-150"
                        >
                          Go back
                        </button>
                      </motion.div>
                    </>
                  )}
                </AnimatePresence>
              </div>
            </>
          )}
          <span className={`
            flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium
            ${workflowStatus === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
              workflowStatus === 'paused' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
              workflowStatus === 'pending_approval' ? 'bg-[#5e6ad2]/[0.12] text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc]' :
              workflowStatus === 'rejected' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
              'bg-muted/70 text-muted-foreground capitalize'}
          `}>
            {WORKFLOW_STATUS_LABELS[workflowStatus] || workflowStatus}
          </span>
          {isDirty && (
            <span
              className="flex-shrink-0 inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400"
              style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
              title="You have unsaved changes"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
              Unsaved changes
            </span>
          )}
        </div>

        <div className="flex items-center gap-2.5 flex-shrink-0">
          {isAiDraft && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDiscard(true)}
              icon={<Trash className="w-4 h-4" />}
              className="rounded-lg border-border/60 text-red-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200 dark:text-red-400 dark:hover:bg-red-500/[0.08] dark:hover:text-red-400 dark:hover:border-red-500/30"
            >
              Discard
            </Button>
          )}
          {workflowId && workflowStatus === 'active' && canActivate && (
            <Button variant="outline" size="sm" onClick={handleDeactivate} icon={<Pause className="w-4 h-4" />} className="rounded-lg border-border/60">
              Pause
            </Button>
          )}
          {workflowId && workflowStatus === 'pending_approval' && (
            <span
              className="inline-flex items-center gap-1.5 px-3 h-8 rounded-lg border border-border/60 text-muted-foreground"
              style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
              title="Waiting for your approver to review this workflow"
            >
              <Clock className="w-4 h-4" />
              Awaiting approval
            </span>
          )}
          {workflowId && workflowStatus !== 'active' && workflowStatus !== 'pending_approval' && canActivate && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => (isRep ? setShowSubmitForApproval(true) : handleActivate())}
              icon={isRep ? <PaperPlaneTilt className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              className="rounded-lg border-border/60"
              // A rejection sticks: only the Rep re-sending it for approval
              // can move it on (the backend refuses a direct activate too).
              disabled={!isRep && workflowStatus === 'rejected'}
              title={
                !isRep && workflowStatus === 'rejected'
                  ? 'Rejected — the requester must edit it and send it for approval again'
                  : undefined
              }
            >
              {isRep ? 'Send for approval' : 'Activate'}
            </Button>
          )}
          {templateAuthoring && canWriteWorkflows && nodes.length > 0 && (
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowSaveAsTemplate(true)}
              icon={<BookmarkSimple className="w-4 h-4" />}
              className="rounded-lg"
            >
              Save as template
            </Button>
          )}
          {!templateAuthoring && (
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                if (saveNeedsResubmitWarning({ isRep, status: workflowStatus, dirty: isDirty || isAiDraft })) {
                  setShowResubmitConfirm(true)
                } else {
                  handleSave()
                }
              }}
              loading={isSaving}
              disabled={!canWriteWorkflows}
              title={canWriteWorkflows ? undefined : 'Your role cannot create or edit workflows'}
              icon={<FloppyDisk className="w-4 h-4" />}
              className="rounded-lg"
            >
              Save
            </Button>
          )}
        </div>
      </div>

      {workflowStatus === 'rejected' && (
        <RejectionBanner approval={workflowApproval} className="rounded-none border-x-0 border-t-0 flex-shrink-0" />
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <WorkflowSidebar />

        {/* Canvas */}
        <div className="flex-1 relative" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={enrichedNodes}
            edges={styledEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onNodeDragStart={takeSnapshot}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onNodesDelete={onNodesDelete}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={{ animated: true, type: 'smoothstep' }}
            fitView
            deleteKeyCode={['Backspace', 'Delete']}
            className="bg-background"
          >
            <Controls />
            <MiniMap
              pannable
              zoomable
              maskColor={isDark ? 'rgba(0, 0, 0, 0.45)' : 'rgba(15, 16, 17, 0.08)'}
              maskStrokeColor={isDark ? 'rgba(255, 255, 255, 0.22)' : 'rgba(94, 106, 210, 0.45)'}
              maskStrokeWidth={2}
              nodeStrokeWidth={3}
              nodeBorderRadius={3}
              style={{
                backgroundColor: isDark ? '#16171a' : '#ffffff',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,16,17,0.10)'}`,
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: isDark
                  ? '0 4px 16px rgba(0,0,0,0.45)'
                  : '0 4px 16px rgba(15,16,17,0.10)',
              }}
              // The MiniMap takes a raw colour, not a class — the catalog's tone
              // hex is the only place these values are allowed to live.
              nodeColor={(n) => nodeHex(n.type)}
            />
            <Background
              variant={BackgroundVariant.Dots}
              gap={16}
              size={1}
              color={isDark ? '#2a2b30' : '#d8dce3'}
            />
          </ReactFlow>

          {/* In-canvas AI chat: continue the generation conversation to edit the workflow */}
          <CanvasChatPanel
            nodes={nodes as any}
            edges={edges as any}
            workflowName={workflowName}
            workflowDescription={workflowDescription}
            initialMessages={aiChatHistory}
            onApply={applyAiEdit}
            onMessagesChange={setAiChatHistory}
          />
        </div>

        {/* Right Config Panel */}
        {selectedNode && (
          <NodeConfigPanel
            node={selectedNode}
            onClose={() => setSelectedNode(null)}
            onUpdate={handleNodeUpdate}
            onDelete={handleNodeDelete}
            nodes={nodes as any}
            edges={edges as any}
            workflowId={workflowId ?? undefined}
          />
        )}
      </div>

      <ConfirmDialog
        open={showResubmitConfirm}
        title="Send changes for approval?"
        description={`This workflow is live. Saving changes to its steps sends it back to ${workflowApproval?.approver_name || 'your approver'} for approval, and campaigns scheduled on it wait until it is approved again. A rename on its own keeps it live.`}
        confirmLabel="Save & send for approval"
        cancelLabel="Keep editing"
        loading={isSaving}
        onConfirm={async () => {
          await handleSave()
          setShowResubmitConfirm(false)
        }}
        onCancel={() => setShowResubmitConfirm(false)}
      />

      <ConfirmDialog
        open={showDiscard}
        title={isAiDraft ? 'Save this workflow?' : 'Discard changes?'}
        description={isAiDraft
          ? 'This workflow hasn’t been saved yet. Leaving without saving will permanently delete it.'
          : 'You have unsaved changes to this workflow. Leaving now will discard them.'}
        confirmLabel="Save & exit"
        cancelLabel="Keep editing"
        loading={isSaving}
        altLabel={isAiDraft ? 'Discard workflow' : 'Discard & exit'}
        altTone="destructive"
        onConfirm={async () => {
          // Primary path: persist the edits, then leave. Stay on a failed or
          // invalid save so nothing is lost (the toast explains what to fix).
          const saved = await handleSave()
          setShowDiscard(false)
          if (saved) exitToList()
        }}
        onAlt={async () => {
          setShowDiscard(false)
          // AI draft was auto-persisted on generation — discarding deletes it.
          if (isAiDraft && workflowId) {
            try { await deleteWorkflow(workflowId) } catch { /* store toasts */ }
          }
          exitToList()
        }}
        onCancel={() => setShowDiscard(false)}
      />

      <SaveAsTemplateDialog
        isOpen={showSaveAsTemplate}
        onClose={() => setShowSaveAsTemplate(false)}
        defaultName={DEFAULT_TEMPLATE_NAME}
        defaultDescription={workflowDescription}
        nodes={nodes as any}
        edges={edges as any}
        onSaved={() => {
          // Force the Templates gallery to refetch rather than rely on it
          // happening to still be stale -- the new template must be visible
          // the moment the user lands back on the list.
          queryClient.invalidateQueries({ queryKey: ['workflow-templates', 'custom'] })
          // This authoring session exists only to produce a template --
          // nothing here was ever persisted as a real workflow, so there's
          // nothing left to stay on the canvas for.
          if (templateAuthoring) navigate('/templates', { state: { tab: 'workflow' } })
        }}
      />

      <SubmitForApprovalDialog
        isOpen={showSubmitForApproval}
        onClose={() => setShowSubmitForApproval(false)}
        workflowName={workflowName}
        preferredApproverId={workflowApproval?.approver_id}
        onSubmit={handleSubmitForApproval}
      />
    </div>
  )
}

const WorkflowBuilderPage: React.FC = () => {
  return (
    <ReactFlowProvider>
      <WorkflowBuilderInner />
    </ReactFlowProvider>
  )
}

export default WorkflowBuilderPage
